import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { LayoutDashboard, ShoppingCart, MessageSquare, Car, LogOut, Menu, X } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout, loading, isAuthenticated } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#00F0FF]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/messages', label: 'Messages', icon: MessageSquare },
    { path: '/admin/custom-requests', label: 'Custom Requests', icon: Car },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#111111] border-b border-[#222222] px-4 py-3">
        <div className="flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_ppf-kit-customizer/artifacts/aut6oyje_PHOTO-2026-02-07-23-08-24.jpg"
            alt="DIY PPF Kit"
            className="h-10 w-auto"
          />
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-[#F8F8F8]">
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#111111] border-r border-[#222222] transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#222222] hidden lg:block">
            <img 
              src="https://customer-assets.emergentagent.com/job_ppf-kit-customizer/artifacts/aut6oyje_PHOTO-2026-02-07-23-08-24.jpg"
              alt="DIY PPF Kit"
              className="h-12 w-auto"
            />
          </div>

          <nav className="flex-1 p-4 mt-16 lg:mt-0">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-none transition-colors ${
                      location.pathname === item.path
                        ? 'bg-[#00F0FF]/10 text-[#00F0FF] border-l-2 border-[#00F0FF]'
                        : 'text-[#A0A0A0] hover:bg-[#1A1A1A] hover:text-[#F8F8F8]'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-[#222222]">
            <div className="mb-4 px-4">
              <p className="text-[#F8F8F8] text-sm font-medium">{admin?.name || 'Admin'}</p>
              <p className="text-[#666666] text-xs truncate">{admin?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-[#333333] text-[#A0A0A0] hover:bg-[#1A1A1A] hover:text-[#F8F8F8] rounded-none"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <Outlet />
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default AdminLayout;
